import type { CardType, Chapter, PlotCard, PlotLine, TimelineState } from '@/pages/timeline/types';
import { supabase } from './supabase';

/**
 * SecureTimelineService
 *
 * A simplified timeline service that uses a single query to fetch data
 * and handles all mutations with proper data transformation.
 */
class SecureTimelineService {
  /**
   * Get the complete timeline data in a single API call
   * @param projectId The project ID
   * @returns The complete timeline data
   */
  async getTimelineData(projectId: string): Promise<any> {
    try {
      console.log(`Getting secure timeline data for project ${projectId}`);

      // Call the RPC function with just the project ID parameter
      const { data, error } = await supabase.rpc('get_secure_timeline_data_1', {
        p_project_id: projectId
      });

      if (error) {
        console.error('Error fetching secure timeline data:', error);
        throw error;
      }

      console.log('Received secure timeline data');
      return data;
    } catch (error) {
      console.error('Error in getTimelineData:', error);
      throw error;
    }
  }

  /**
   * Transform the raw timeline data into the TimelineState format
   * @param secureData Raw data from the RPC function
   * @param projectId Project ID
   * @returns The timeline state object
   */
  transformTimelineData(secureData: any, projectId: string): TimelineState {
    if (!secureData) {
      throw new Error('No secure timeline data provided');
    }

    // Get the primary timeline or first timeline
    const primaryTimeline = secureData.timeline?.find((t: any) => t.is_primary) || secureData.timeline?.[0];

    if (!primaryTimeline) {
      throw new Error('No timeline found in secure data');
    }

    // Map the timeline array to plotLines
    const plotLines = (secureData.timeline || []).map((line: any) => ({
      id: parseInt(line.id),
      name: line.title || 'Untitled',
      description: line.description || '',
      color: '#4299e1', // Default color if not provided
      created_at: line.created_at,
      updated_at: line.updated_at,
      _order: line.display_order || 0,
      isPrimary: line.is_primary
    }));

    // Map the chapters with title → name
    const chapters = (secureData.chapters || []).map((chapter: any) => {
      console.log('=== SECURE TIMELINE CHAPTER DEBUG ===');
      console.log('Raw chapter from secure data:', chapter);
      console.log('Chapter description:', chapter.description);
      console.log('=== END SECURE TIMELINE CHAPTER DEBUG ===');

      return {
        id: chapter.id,
        name: chapter.title || 'Untitled Chapter',
        title: chapter.title || 'Untitled Chapter',
        description: chapter.description || '',
        created_at: chapter.created_at,
        updated_at: chapter.created_at,
        _order: chapter.display_order || 0
      };
    });

    // Map the plotcards with description → content and include entities and tags
    const plotCards = (secureData.plotcards || []).map((card: any) => {
      // Debug logging for entities and tags
      if (card.id % 10 === 0) { // Only log every 10th card to avoid console spam
        console.log(`Processing card ${card.id} (${card.title})`);
        console.log(`  Entities: ${JSON.stringify(card.entities)}`);
        console.log(`  Tags: ${JSON.stringify(card.tags)}`);
      }

      return {
        id: card.id,
        title: card.title || 'Untitled',
        content: card.description || '',
        type: card.metadata?.type || 'plot',
        x: parseInt(card.timeline_id),
        y: card.chapter_id || 0,
        value: card.description || '',
        created_at: card.created_at,
        updated_at: card.updated_at,
        chapter_id: card.chapter_id || null,
        position: card.position || 0,
        color: card.color,
        entities: Array.isArray(card.entities) ? card.entities : [],
        tags: Array.isArray(card.tags) ? card.tags : []
      };
    });

    // Get settings or use default settings
    const settings = secureData.settings || {
      id: 'temp-' + Date.now(),
      timeline_id: primaryTimeline.id,
      zoom_level: 1,
      display_mode: 'horizontal' as const,
      show_chapter_markers: true,
      color_scheme: {
        'character': '#4299e1',
        'event': '#ed8936',
        'location': '#48bb78',
        'item': '#9f7aea',
        'note': '#f6e05e'
      }
    };

    // Create the TimelineState object
    const result: TimelineState = {
      storyId: primaryTimeline.story_id.toString(),
      projectId: projectId,
      plotLines,
      chapters,
      plotCards,
      settings: settings,
      timeline: primaryTimeline
    };

    return result;
  }

  /**
   * Get the complete timeline state in a single call
   * @param projectId The project ID
   * @returns The timeline state
   */
  async getTimelineState(projectId: string): Promise<TimelineState> {
    try {
      console.log('=== Fetching timeline data in a single call for project:', projectId);

      // Call the RPC function to get all data at once
      const secureData = await this.getTimelineData(projectId);

      if (!secureData) {
        console.error('No data returned from secure timeline data function');
        throw new Error('No timeline data found');
      }

      // Transform the secure data into TimelineState format
      const timelineState = this.transformTimelineData(secureData, projectId);

      console.log('=== Created timeline state with:', {
        timelinesCount: timelineState.plotLines.length,
        plotCardsCount: timelineState.plotCards.length,
        chaptersCount: timelineState.chapters.length
      });

      return timelineState;
    } catch (error) {
      console.error('Error in getTimelineState:', error);
      throw error;
    }
  }

  /**
   * Create a plot card
   *
   * To do: Both plotLineId and timelineId are same. and need to remove timelineId from the function.
   */
  async createPlotCard(
    timelineId: string,
    plotLineId: string,
    title: string,
    content: string,
    cardType: CardType = 'note',
    chapterId?: number,
    position?: number
  ): Promise<PlotCard> {
    try {
      console.log('=== Creating plot card with params:', {
        timelineId,
        plotLineId,
        title,
        content,
        cardType,
        chapterId,
        position
      });

      // Use the plotcards table
      const { data, error } = await supabase
        .from('plotcards')
        .insert([
          {
            timeline_id: plotLineId,
            title,
            description: content,
            metadata: {
              type: cardType,
              plotLineId
            },
            chapter_id: chapterId || null,
            position: position || null
          }
        ])
        .select()
        .single();

      if (error) {
        console.error('=== Error creating plot card:', error);
        throw error;
      }

      // Transform the data to match the PlotCard interface
      const cardData = data as any;
      const metadata = cardData.metadata || {};

      const plotCard: PlotCard = {
        id: cardData.id,
        title: cardData.title || 'Untitled',
        content: cardData.description || '',
        type: cardType,
        x: parseInt(plotLineId),
        y: cardData.chapter_id || 0,
        value: cardData.description || '',
        created_at: cardData.created_at,
        updated_at: cardData.updated_at,
        chapter_id: cardData.chapter_id || null,
        position: cardData.position || null
      };

      console.log('=== Created plot card:', plotCard);

      return plotCard;
    } catch (error) {
      console.error('=== Error in createPlotCard:', error);
      throw error;
    }
  }

  /**
   * Update a plot card
   */
  async updatePlotCard(
    cardId: string,
    updates: Partial<PlotCard>,
    timelineId?: string
  ): Promise<PlotCard> {
    try {
      console.log('=== Updating plot card:', cardId, 'with updates:', updates);

      // Note: We've simplified this function to avoid fetching the current card before updating it.
      // This is more efficient for drag and drop operations where we already have all the necessary information.
      // The tradeoff is that we'll replace the entire metadata object rather than merging with existing fields.
      // For our use case with drag and drop, this is acceptable since we only care about type and plotLineId.

      // Determine the timeline ID to use
      // If updates.x is provided, use that; otherwise use the provided timelineId
      const effectiveTimelineId = updates.x !== undefined ? updates.x : (timelineId ? parseInt(timelineId) : undefined);

      // Prepare the updates for the database
      // Use jsonb_set to preserve existing metadata fields
      const dbUpdates: any = {};

      // Map the PlotCard fields to database fields
      if (updates.title !== undefined) dbUpdates.title = updates.title;
      if (updates.content !== undefined) dbUpdates.description = updates.content;

      // Build metadata updates
      // For drag and drop operations, we need to make sure we preserve the card type
      const metadataUpdates: any = {
        // Always include a default type of 'note' to ensure we have a type
        // This will be overridden if updates.type is provided
        type: 'note'
      };

      // If a specific type is provided in the updates, use that instead
      if (updates.type !== undefined) {
        metadataUpdates.type = updates.type;
      }

      // IMPORTANT: Update both timeline_id column AND plotLineId in metadata
      if (effectiveTimelineId !== undefined) {
        dbUpdates.timeline_id = effectiveTimelineId;
        metadataUpdates.plotLineId = effectiveTimelineId.toString();
        console.log('=== Updating both timeline_id and plotLineId to:', effectiveTimelineId);
      }

      // Always include metadata updates to ensure the type is preserved
      // This is critical for drag and drop operations to maintain the card type
      // Note: This will replace the entire metadata object, which means any other
      // metadata fields not specified here will be lost. For our use case, this is
      // acceptable since we only care about the type and plotLineId fields.
      dbUpdates.metadata = metadataUpdates;

      // Handle y-coordinate as chapter_id
      if (updates.y !== undefined) dbUpdates.chapter_id = updates.y;

      // Also handle direct chapter_id updates
      if (updates.chapter_id !== undefined) dbUpdates.chapter_id = updates.chapter_id;

      // Handle position updates
      if (updates.position !== undefined) dbUpdates.position = updates.position;

      // Update the card
      const { data: updatedCard, error: updateError } = await supabase
        .from('plotcards')
        .update(dbUpdates)
        .eq('id', cardId)
        .select()
        .single();

      if (updateError) {
        console.error('Error updating plot card:', updateError);
        throw updateError;
      }

      if (!updatedCard) {
        throw new Error('Failed to update plot card');
      }

      // Transform the updated data to match the PlotCard interface
      const metadata = updatedCard.metadata || {};
      const plotLineId = metadata.plotLineId || updatedCard.timeline_id?.toString() || '0';
      const cardType = metadata.type || 'note';

      const plotCard: PlotCard = {
        id: updatedCard.id,
        title: updatedCard.title || 'Untitled',
        content: updatedCard.description || '',
        type: cardType as CardType,
        x: parseInt(plotLineId),
        y: updatedCard.chapter_id || 0,
        value: updatedCard.description || '',
        created_at: updatedCard.created_at,
        updated_at: updatedCard.updated_at,
        chapter_id: updatedCard.chapter_id || null,
        position: updatedCard.position || null
      };

      return plotCard;
    } catch (error) {
      console.error('Error in updatePlotCard:', error);
      throw error;
    }
  }

  /**
   * Delete a plot card
   */
  async deletePlotCard(cardId: string, timelineId: string): Promise<void> {
    try {
      // Delete the card
      const { error } = await supabase
        .from('plotcards')
        .delete()
        .eq('id', cardId);

      if (error) {
        console.error('Error deleting plot card:', error);
        throw error;
      }

      console.log('=== Successfully deleted plot card');
    } catch (error) {
      console.error('Error in deletePlotCard:', error);
      throw error;
    }
  }

  /**
   * Create a new plot line (timeline)
   */
  async createPlotLine(
    storyId: string,
    title: string,
    color: string = '#4299e1'
  ): Promise<PlotLine> {
    try {
      // Check if storyId is valid
      if (!storyId) {
        console.error('Error creating plot line: storyId is null or undefined');
        throw new Error('Story ID is required to create a timeline');
      }

      console.log('=== Creating plot line:', title, 'for story:', storyId);

      // Create a new timeline record - ensure story_id is properly parsed
      const { data, error } = await supabase
        .from('timelines')
        .insert([
          {
            story_id: parseInt(storyId),
            title: title,
            description: ''
          }
        ])
        .select()
        .single();

      if (error) {
        console.error('Error creating plot line:', error);
        throw error;
      }

      // Transform the data to match the PlotLine interface
      const plotLine: PlotLine = {
        id: data.id,
        name: data.title || 'Untitled',
        description: data.description || '',
        color: color,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      return plotLine;
    } catch (error) {
      console.error('Error in createPlotLine:', error);
      throw error;
    }
  }

  /**
   * Update a plot line
   */
  async updatePlotLine(
    lineId: string,
    updates: Partial<PlotLine>
  ): Promise<PlotLine> {
    try {
      // Prepare the updates for the database
      const dbUpdates: any = {};

      // Map the PlotLine fields to database fields
      if (updates.name !== undefined) dbUpdates.title = updates.name;
      if (updates.description !== undefined) dbUpdates.description = updates.description;

      // Update the timeline
      const { data, error } = await supabase
        .from('timelines')
        .update(dbUpdates)
        .eq('id', lineId)
        .select()
        .single();

      if (error) {
        console.error('Error updating plot line:', error);
        throw error;
      }

      // Transform the data to match the PlotLine interface
      const plotLine: PlotLine = {
        id: data.id,
        name: data.title || 'Untitled',
        description: data.description || '',
        color: '#4299e1', // Default color
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      return plotLine;
    } catch (error) {
      console.error('Error in updatePlotLine:', error);
      throw error;
    }
  }

  /**
   * Delete a plot line
   */
  async deletePlotLine(lineId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('timelines')
        .delete()
        .eq('id', lineId);

      if (error) {
        console.error('Error deleting plot line:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in deletePlotLine:', error);
      throw error;
    }
  }

  /**
   * Update timeline settings
   */
  async updateTimelineSettings(
    timelineId: string,
    updates: any
  ): Promise<any> {
    try {
      console.log('Updating timeline settings for timeline:', timelineId);

      // First, check if settings exist
      const { data: existingSettings, error: fetchError } = await supabase
        .from('timeline_settings')
        .select('*')
        .eq('timeline_id', timelineId)
        .maybeSingle();

      if (fetchError) {
        console.error('Error fetching settings:', fetchError);
        throw fetchError;
      }

      // If settings don't exist, create them first
      if (!existingSettings) {
        console.log('No settings found, creating default settings before updating');

        const defaultSettings = {
          timeline_id: timelineId,
          zoom_level: 1,
          display_mode: 'horizontal',
          show_chapter_markers: true,
          color_scheme: {
            'character': '#4299e1',
            'event': '#ed8936',
            'location': '#48bb78',
            'item': '#9f7aea',
            'note': '#f6e05e'
          }
        };

        await supabase
          .from('timeline_settings')
          .insert([defaultSettings]);
      }

      // Now update the settings
      const { data, error } = await supabase
        .from('timeline_settings')
        .update(updates)
        .eq('timeline_id', timelineId)
        .select()
        .single();

      if (error) {
        console.error('Error updating timeline settings:', error);
        throw error;
      }

      console.log('Successfully updated timeline settings:', data);
      return data;
    } catch (error) {
      console.error('Error in updateTimelineSettings:', error);
      throw error;
    }
  }

  /**
   * Move cards to a chapter
   */
  async moveCardsToChapter(
    cardIds: number[],
    chapterId: number | null,
    timelineId: string
  ): Promise<PlotCard[]> {
    try {
      console.log('=== Moving cards to chapter:', chapterId, 'cards:', cardIds);

      if (!cardIds.length) {
        console.log('=== No cards to move');
        return [];
      }

      // Update all cards with the new chapter_id
      const { error } = await supabase
        .from('plotcards')
        .update({
          chapter_id: chapterId,
          timeline_id: timelineId
        })
        .in('id', cardIds);

      if (error) {
        console.error('Error moving cards to chapter:', error);
        throw error;
      }

      // Fetch the updated cards to return them
      const { data: updatedCards, error: fetchError } = await supabase
        .from('plotcards')
        .select('*')
        .in('id', cardIds);

      if (fetchError) {
        console.error('Error fetching updated cards:', fetchError);
        throw fetchError;
      }

      if (!updatedCards || updatedCards.length === 0) {
        console.log('=== No updated cards found');
        return [];
      }

      // Transform the data to match the PlotCard interface
      const plotCards: PlotCard[] = updatedCards.map(item => {
        const metadata = item.metadata || {};
        const plotLineId = metadata.plotLineId || item.timeline_id?.toString() || '0';
        const cardType = metadata.type || 'note';

        return {
          id: item.id,
          title: item.title || 'Untitled',
          content: item.description || '',
          type: cardType as CardType,
          x: parseInt(plotLineId),
          y: item.chapter_id || 0,
          value: item.description || '',
          created_at: item.created_at,
          updated_at: item.updated_at,
          chapter_id: item.chapter_id || null,
          position: item.position || null
        };
      });

      console.log(`=== Successfully moved ${plotCards.length} cards to chapter ${chapterId}`);
      return plotCards;
    } catch (error) {
      console.error('Error in moveCardsToChapter:', error);
      throw error;
    }
  }

  /**
   * Update plot card position
   */
  async updatePlotCardPosition(
    cardId: string,
    newPosition: number,
    timelineId: string,
    chapterId: number | null
  ): Promise<PlotCard> {
    try {
      console.log('=== Updating plot card position:', cardId, 'to position:', newPosition);

      // Update the card with the new position
      const { data, error } = await supabase
        .from('plotcards')
        .update({
          position: newPosition
        })
        .eq('id', cardId)
        .select()
        .single();

      if (error) {
        console.error('Error updating plot card position:', error);
        throw error;
      }

      // Transform the data to match the PlotCard interface
      const cardData = data as any;
      const metadata = cardData.metadata || {};
      const plotLineId = metadata.plotLineId || cardData.timeline_id?.toString() || '0';
      const cardType = metadata.type || 'note';

      const plotCard: PlotCard = {
        id: cardData.id,
        title: cardData.title || 'Untitled',
        content: cardData.description || '',
        type: cardType as CardType,
        x: parseInt(plotLineId),
        y: cardData.chapter_id || 0,
        value: cardData.description || '',
        created_at: cardData.created_at,
        updated_at: cardData.updated_at,
        chapter_id: cardData.chapter_id || null,
        position: cardData.position || null
      };

      console.log('=== Updated plot card position:', plotCard);
      return plotCard;
    } catch (error) {
      console.error('Error in updatePlotCardPosition:', error);
      throw error;
    }
  }

  /**
   * Reorder chapters using a batch operation
   */
  async reorderChapters(storyId: string, chapterIds: number[]): Promise<Chapter[]> {
    try {
      // Check if storyId is valid
      if (!storyId) {
        console.error('Error reordering chapters: storyId is null or undefined');
        throw new Error('Story ID is required to reorder chapters');
      }

      console.log('=== Batch reordering chapters for story:', storyId, chapterIds);

      // Use the reorder_chapters RPC function (void return)
      const { error } = await supabase
        .rpc('reorder_chapters', {
          p_story_id: parseInt(storyId),
          p_chapter_ids: chapterIds
        });

      // Handle errors from the RPC call
      if (error) {
        console.error('Error batch reordering chapters:', error);
        throw new Error(`Failed to reorder chapters: ${error.message}`);
      }

      console.log('=== Chapters batch reordered successfully using RPC function');

      // Fetch the updated chapters to return them
      const { data: chapters, error: fetchError } = await supabase
        .from('chapters')
        .select('*')
        .eq('story_id', storyId)
        .order('display_order', { ascending: true });

      if (fetchError) {
        throw new Error(`Failed to fetch reordered chapters: ${fetchError.message}`);
      }

      // Transform the returned chapters to match our Chapter interface
      const reorderedChapters: Chapter[] = (chapters || []).map((chapter: any, index: number) => ({
        id: chapter.id,
        name: chapter.title || 'Untitled Chapter',
        title: chapter.title || 'Untitled Chapter',
        description: chapter.description || '',
        created_at: chapter.created_at,
        updated_at: chapter.last_saved_at || chapter.created_at,
        _order: chapter.display_order || index + 1
      }));

      return reorderedChapters;
    } catch (error) {
      console.error('Error in reorderChapters:', error);
      throw error;
    }
  }

  /**
   * Reorder timelines using a batch operation
   */
  async reorderTimelines(storyId: string, timelineIds: number[]): Promise<PlotLine[]> {
    try {
      // Check if storyId is valid
      if (!storyId) {
        console.error('Error reordering timelines: storyId is null or undefined');
        throw new Error('Story ID is required to reorder timelines');
      }

      console.log('=== Batch reordering timelines for story:', storyId, timelineIds);

      // Use the batch_reorder_timelines RPC function
      const { data, error } = await supabase
        .rpc('batch_reorder_timelines', {
          p_story_id: parseInt(storyId),
          p_timeline_ids: timelineIds
        });

      // Handle errors from the RPC call
      if (error) {
        console.error('Error batch reordering timelines:', error);
        throw new Error(`Failed to reorder timelines: ${error.message}`);
      }

      // Validate the response
      if (!data || !data.success) {
        console.error('Batch reorder operation failed:', data);
        throw new Error('Failed to reorder timelines: Invalid response from server');
      }

      console.log('=== Timelines batch reordered successfully using RPC function');

      // Transform the returned timelines to match our PlotLine interface
      const reorderedTimelines: PlotLine[] = data.timelines.map((timeline: any, index: number) => ({
        id: timeline.id,
        name: timeline.title || 'Untitled',
        title: timeline.title || 'Untitled',
        description: timeline.description || '',
        color: '#4299e1', // Default color
        created_at: timeline.created_at,
        updated_at: timeline.updated_at,
        is_primary: timeline.is_primary || false,
        _order: timeline.display_order || index
      }));

      return reorderedTimelines;
    } catch (error) {
      console.error('Error in reorderTimelines:', error);
      throw error;
    }
  }

}

export const secureTimelineService = new SecureTimelineService();